import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { formatTime as formatTimeUtil } from '../../utils/dateFormatting';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

// Safely extract error message from FastAPI responses (handles 422 detail arrays)
const toastApiError = (e, fallback = 'Operation failed') => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') toast.error(detail);
  else if (Array.isArray(detail)) toast.error(detail.map(d => d.msg || JSON.stringify(d)).join('; '));
  else toast.error(fallback);
};
import {
  ChevronLeft,
  Calendar,
  Loader2,
  MapPin,
  Truck,
  User,
  Building2,
  Clock,
  Pencil,
  Check,
  X,
  Users,
  Save,
  CheckCircle2,
  AlertTriangle,
  Trash2,
  Copy,
  Package,
  Flag,
  Navigation,
  Plus,
  Bell,
  RotateCcw,
} from 'lucide-react';
import { format, eachDayOfInterval, parseISO } from 'date-fns';
import MobileLayout from '../../components/mobile/MobileLayout';
import { WheelTimePicker } from '../../components/mobile/WheelTimePicker';
import { DatePicker } from '../../components/ui/date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../../components/ui/sheet';
import { GooglePlacesAutocomplete } from '../../components/ui/google-places-autocomplete';
import { AddRouteStopDialog } from '../../components/mobile/AddRouteStopDialog';
import { EditRouteStopDialog } from '../../components/mobile/EditRouteStopDialog';
import { StopArrivalBadge } from '../../components/StopArrivalBadge';
import { CompleteStopDialog } from '../../components/mobile/CompleteStopDialog';
import { DeleteStopDialog } from '../../components/mobile/DeleteStopDialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';

const resolveNotifyTemplate = (str, vars) => {
  let result = str || '';
  Object.entries(vars).forEach(([k, v]) => {
    if (k === 'operator_name') return; // resolved per-worker on the backend
    result = result.replace(new RegExp(`\\{${k}\\}`, 'g'), v || '');
  });
  return result;
};

const buildVariables = (job) => {
  if (!job) return {};
  return {
    job_number: job.job_number || job.id?.slice(-6) || '',
    site_name: job.site_name || job.location || '',
    customer_name: job.customer_name || job.client || '',
    company_name: job.customer_name || '',
    date: job.start_date || '',
    start_time: job.start_time || '',
    depot_time: job.depot_time || '',
    description: job.description || '',
    equipment_name: job.equipment_type || '',
  };
};

const MobileScheduleJobDetail = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const { api, tenantSettings, operator } = useMobileAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completingStop, setCompletingStop] = useState(null);
  const [deletingStop, setDeletingStop] = useState(null);
  const [saving, setSaving] = useState(false);
  const timeFormat = tenantSettings?.time_format || '24h';

  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [operators, setOperators] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [workerTypes, setWorkerTypes] = useState([]);
  const [workerAllocations, setWorkerAllocations] = useState([]);
  const [jobDayDetails, setJobDayDetails] = useState({});
  const [dayEditOpen, setDayEditOpen] = useState(false);
  const [dayEditDate, setDayEditDate] = useState(null);
  const [dayEditForm, setDayEditForm] = useState({ start_time: '', end_time: '' });
  const [dayEditSaving, setDayEditSaving] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showAllocationSheet, setShowAllocationSheet] = useState(false);
  const [allocationForm, setAllocationForm] = useState({
    equipment_id: '', operator_id: '', start_date: '', end_date: '',
    is_combo_allocation: false, combo_equipment_ids: [],
    worker_allocations: [],
    status: 'confirmed'
  });
  const [editingAllocationId, setEditingAllocationId] = useState(null);
  const [showEmailSheet, setShowEmailSheet] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [emailSelected, setEmailSelected] = useState({});
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailTemplateId, setEmailTemplateId] = useState('');
  const [pushTitle, setPushTitle] = useState('');
  const [pushMessage, setPushMessage] = useState('');
  const [workerAckEnabled, setWorkerAckEnabled] = useState(false);
  const [notifyOperatorEnabled, setNotifyOperatorEnabled] = useState(false);
  const [ackBusyId, setAckBusyId] = useState(null);

  const emailTimeAgo = (iso) => {
    if (!iso) return null;
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const applyTemplate = (t) => {
    if (!t) return;
    setEmailTemplateId(t.id);
    const vars = buildVariables(job);
    setPushTitle(resolveNotifyTemplate(t.title_template, vars));
    setPushMessage(resolveNotifyTemplate(t.message_template, vars));
  };

  const loadEmailRecipients = async () => {
    setEmailLoading(true);
    try {
      const res = await api.get(`/api/jobs/${jobId}/notify-recipients`);
      const recs = res.data?.recipients || [];
      setEmailRecipients(recs);
      const preset = {};
      recs.forEach(r => { preset[r.operator_id] = !r.last_emailed_at; });
      setEmailSelected(preset);
    } catch (e) {
      toastApiError(e, 'Failed to load recipients');
    } finally {
      setEmailLoading(false);
    }

    // Load push templates (best-effort — falls back to a sensible default).
    const seedDefault = () => {
      const vars = buildVariables(job);
      setPushTitle('New job assigned');
      setPushMessage(resolveNotifyTemplate('Job #{job_number} at {site_name} on {date}, start {start_time}. Tap for details.', vars));
    };
    try {
      const tRes = await api.get('/api/notification-templates');
      const tmpls = (tRes.data?.templates || []).filter(t => t.is_active);
      setEmailTemplates(tmpls);
      const preferred = tmpls.find(t => t.notification_type === 'job_assigned') || tmpls[0];
      if (preferred) applyTemplate(preferred);
      else seedDefault();
    } catch {
      seedDefault();
    }
  };

  const openEmailSheet = () => { setShowEmailSheet(true); loadEmailRecipients(); };

  const handleSendWorkerEmails = async () => {
    const chosen = emailRecipients.filter(r => emailSelected[r.operator_id]);
    if (chosen.length === 0) { toast.error('Select at least one worker'); return; }
    setEmailSending(true);
    try {
      const res = await api.post(`/api/jobs/${jobId}/notify-workers`, {
        operator_ids: chosen.map(r => r.operator_id),
        push_title: pushTitle,
        push_message: pushMessage,
      });
      const notified = res.data?.notified || [];
      const skipped = res.data?.skipped_no_email || [];
      const failed = res.data?.failed || [];
      if (notified.length) toast.success(`Notified ${notified.length} worker(s)`);
      if (skipped.length) toast.warning(`No email address: ${skipped.join(', ')}`);
      if (failed.length) toast.error(`Failed to notify: ${failed.join(', ')}. Check email settings.`);
      if (!notified.length && !skipped.length && !failed.length) toast.info('Nothing to send.');
      await loadEmailRecipients();
    } catch (e) {
      toastApiError(e, 'Failed to send');
    } finally {
      setEmailSending(false);
    }
  };

  // Resolve worker-notification feature flags for the button gate + push UI.
  useEffect(() => {
    let active = true;
    api.get('/api/feature-flags/resolved')
      .then(res => {
        if (!active) return;
        const flags = res.data?.flags || {};
        setWorkerAckEnabled(!!flags.worker_job_confirmation);
        setNotifyOperatorEnabled(!!flags.notify_operator);
      })
      .catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteAllocDialog, setShowDeleteAllocDialog] = useState(false);
  const [deletingAllocId, setDeletingAllocId] = useState(null);

  useEffect(() => { fetchData(); }, [jobId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [jobRes, customersRes, sitesRes, eqRes, opRes, typesRes, allocRes, wtRes, waRes, dayDetailsRes] = await Promise.all([
        api.get(`/api/jobs/${jobId}`),
        api.get('/api/customers'),
        api.get('/api/sites'),
        api.get('/api/equipment'),
        api.get('/api/operators'),
        api.get('/api/equipment-types/all'),
        api.get('/api/allocations'),
        api.get('/api/worker-types').catch(() => ({ data: [] })),
        api.get('/api/worker-allocations').catch(() => ({ data: [] })),
        api.get(`/api/job-day-details/job/${jobId}`).catch(() => ({ data: [] }))
      ]);
      setJob(jobRes.data);
      setCustomers(customersRes.data || []);
      setSites(sitesRes.data || []);
      setEquipment(eqRes.data || []);
      setOperators(opRes.data || []);
      setEquipmentTypes(typesRes.data || []);
      setAllocations(allocRes.data || []);
      setWorkerTypes(wtRes.data || []);
      setWorkerAllocations(waRes.data || []);
      const detailsByDate = {};
      (dayDetailsRes.data || []).forEach(d => { detailsByDate[d.date] = d; });
      setJobDayDetails(detailsByDate);
      setEditForm({
        customer_id: jobRes.data.customer_id || '',
        site_id: jobRes.data.site_id || '',
        equipment_type: jobRes.data.equipment_type || '',
        start_date: jobRes.data.start_date || '',
        end_date: jobRes.data.end_date || '',
        start_time: jobRes.data.start_time || '',
        description: jobRes.data.description || '',
        notes: jobRes.data.notes || '',
        starting_location: jobRes.data.starting_location || ''
      });

      // Trigger backend status recalculation for stale combo-asset statuses
      const currentStatus = jobRes.data.status;
      if (currentStatus && !['docketed', 'invoiced'].includes(currentStatus)) {
        api.post(`/api/jobs/${jobId}/recalculate-status`).then(res => {
          if (res.data?.changed) {
            setJob(prev => prev ? { ...prev, status: res.data.status } : prev);
          }
        }).catch(() => {}); // Non-critical, silently ignore
      }
    } catch (error) {
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const getCustomerName = (id) => customers.find(c => c.id === id)?.company_name || 'Unknown';
  const getSiteName = (id) => { const s = sites.find(x => x.id === id); return s?.name || s?.street_address || ''; };
  const getSiteAddress = (id) => { const s = sites.find(x => x.id === id); if (!s) return ''; return [s.street_address, s.suburb, s.state, s.postcode].filter(Boolean).join(', '); };
  const getCustomerSites = (custId) => sites.filter(s => s.customer_id === custId);
  const getEquipmentName = (id) => { const eq = equipment.find(e => e.id === id); return eq ? `${eq.asset_id} (${eq.equipment_type})` : ''; };
  const getOperatorName = (id) => operators.find(o => o.id === id)?.name || '';

  // Admin manual override — mark a sent/re-ack allocation as confirmed on behalf of the worker.
  const adminConfirmAlloc = async (alloc) => {
    setAckBusyId(alloc.id);
    try {
      await api.post(`/api/allocations/${alloc.id}/admin-confirm`, { reason: 'Confirmed by admin from mobile Job Detail' });
      toast.success(`Marked ${getOperatorName(alloc.operator_id) || 'worker'} as confirmed`);
      await fetchData();
    } catch (err) {
      toast.error('Confirm failed', { description: err.response?.data?.detail || err.message });
    } finally {
      setAckBusyId(null);
    }
  };

  // Get ALL allocations for this job (multi-day can have multiple blocks)
  const jobAllocations = useMemo(() =>
    allocations.filter(a => a.job_id === jobId).sort((a, b) => (a.start_date || '').localeCompare(b.start_date || '')),
    [allocations, jobId]
  );

  // Check if equipment type is combo
  const isComboType = useMemo(() => {
    if (!job?.equipment_type) return false;
    const eqType = equipmentTypes.find(t =>
      (typeof t === 'string' ? t : t?.name) === job.equipment_type
    );
    return typeof eqType === 'object' ? !!eqType?.is_combo : false;
  }, [job?.equipment_type, equipmentTypes]);

  // Get combo components for the job's equipment type
  const comboComponents = useMemo(() => {
    if (!isComboType || !job?.equipment_type) return [];
    const eqType = equipmentTypes.find(t => t?.name === job.equipment_type);
    if (!eqType?.combo_component_ids?.length) return [];
    return eqType.combo_component_ids.map(id => {
      const comp = equipmentTypes.find(t => t?.id === id);
      return comp ? { id: comp.id, name: comp.name } : null;
    }).filter(Boolean);
  }, [isComboType, job?.equipment_type, equipmentTypes]);

  // Get equipment sorted with matching type first, then all others
  const getEquipmentForType = (typeName) => {
    if (!typeName) return equipment;
    const matching = equipment.filter(e => e.equipment_type === typeName);
    const others = equipment.filter(e => e.equipment_type !== typeName);
    return [...matching, ...others];
  };

  // Multi-day coverage stats
  const coverageStats = useMemo(() => {
    if (!job || !job.end_date || job.end_date === job.start_date) return null;
    try {
      const allDates = eachDayOfInterval({
        start: parseISO(job.start_date),
        end: parseISO(job.end_date)
      });
      const excluded = new Set(job.excluded_dates || []);
      const activeDates = allDates.filter(d => !excluded.has(format(d, 'yyyy-MM-dd')));
      const coveredDates = new Set();
      jobAllocations.forEach(a => {
        if (!a.start_date) return;
        const aEnd = a.end_date || a.start_date;
        try {
          eachDayOfInterval({ start: parseISO(a.start_date), end: parseISO(aEnd) })
            .forEach(d => coveredDates.add(format(d, 'yyyy-MM-dd')));
        } catch {}
      });
      const covered = activeDates.filter(d => coveredDates.has(format(d, 'yyyy-MM-dd'))).length;
      return { total: activeDates.length, covered };
    } catch { return null; }
  }, [job, jobAllocations]);

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/api/jobs/${jobId}`, { ...job, ...editForm, end_date: editForm.end_date || null });
      toast.success('Job updated');
      setIsEditing(false);
      fetchData();
    } catch (error) {
      toastApiError(error, 'Failed to update job');
    } finally { setSaving(false); }
  };

  // Active days of a multi-day job (start..end, excluding excluded_dates)
  const activeDays = useMemo(() => {
    if (!job || !job.start_date) return [];
    const end = job.end_date || job.start_date;
    if (end === job.start_date) return [job.start_date];
    try {
      const excluded = new Set(job.excluded_dates || []);
      return eachDayOfInterval({ start: parseISO(job.start_date), end: parseISO(end) })
        .map(d => format(d, 'yyyy-MM-dd'))
        .filter(ds => !excluded.has(ds));
    } catch { return []; }
  }, [job]);

  // Resolve effective start/end time for a given day, honouring per-day overrides
  const resolveDayTimes = (dateStr) => {
    const o = jobDayDetails[dateStr];
    return {
      start_time: o?.start_time || job?.start_time || '',
      end_time: o?.end_time || job?.end_time || '',
      overridden: !!(o?.start_time || o?.end_time),
    };
  };

  const openDayEdit = (dateStr) => {
    const o = jobDayDetails[dateStr];
    setDayEditDate(dateStr);
    setDayEditForm({ start_time: o?.start_time || '', end_time: o?.end_time || '' });
    setDayEditOpen(true);
  };

  const handleSaveDayTime = async () => {
    if (!dayEditDate) return;
    setDayEditSaving(true);
    try {
      await api.post('/api/job-day-details', {
        job_id: jobId,
        date: dayEditDate,
        start_time: dayEditForm.start_time || null,
        end_time: dayEditForm.end_time || null,
      });
      toast.success('Day time saved');
      setDayEditOpen(false);
      const res = await api.get(`/api/job-day-details/job/${jobId}`).catch(() => ({ data: [] }));
      const detailsByDate = {};
      (res.data || []).forEach(d => { detailsByDate[d.date] = d; });
      setJobDayDetails(detailsByDate);
    } catch (error) {
      toastApiError(error, 'Failed to save day time');
    } finally { setDayEditSaving(false); }
  };

  const handleResetDayTime = async () => {
    if (!dayEditDate) return;
    const existing = jobDayDetails[dayEditDate];
    if (!existing?.id) { setDayEditOpen(false); return; }
    setDayEditSaving(true);
    try {
      await api.delete(`/api/job-day-details/${existing.id}`);
      toast.success('Reset to job default');
      setDayEditOpen(false);
      const res = await api.get(`/api/job-day-details/job/${jobId}`).catch(() => ({ data: [] }));
      const detailsByDate = {};
      (res.data || []).forEach(d => { detailsByDate[d.date] = d; });
      setJobDayDetails(detailsByDate);
    } catch (error) {
      toastApiError(error, 'Failed to reset day time');
    } finally { setDayEditSaving(false); }
  };

  const handleOpenAllocation = (existingAlloc = null) => {
    const isCombo = isComboType;
    const defaultStatus = tenantSettings?.auto_confirm_jobs !== false ? 'confirmed' : 'draft';

    // Build worker allocation slots from job requirements
    const jobWAs = workerAllocations.filter(wa => wa.job_id === jobId);
    const buildWorkerSlots = () => {
      return (job.worker_requirements || []).map(wr => {
        const existing = jobWAs.find(wa => wa.worker_type_id === wr.worker_type_id);
        const wt = workerTypes.find(t => t.id === wr.worker_type_id);
        return {
          worker_type_id: wr.worker_type_id,
          worker_type_name: wt?.name || 'Worker',
          operator_id: existing?.operator_id || '',
          id: existing?.id,
          quantity: wr.quantity || 1
        };
      });
    };

    if (existingAlloc) {
      // Editing an existing allocation
      let comboEquipIds = [];
      if (isCombo && comboComponents.length > 0) {
        const existing = existingAlloc.combo_equipment_ids || [];
        comboEquipIds = comboComponents.map((comp, idx) => ({
          component_type_id: comp.id,
          component_type_name: comp.name,
          equipment_id: typeof existing[idx] === 'string' ? existing[idx] : (existing[idx]?.equipment_id || '')
        }));
      }
      setAllocationForm({
        equipment_id: existingAlloc.equipment_id || '',
        operator_id: existingAlloc.operator_id || '',
        start_date: existingAlloc.start_date || job.start_date,
        end_date: existingAlloc.end_date || '',
        is_combo_allocation: existingAlloc.is_combo_allocation || isCombo,
        combo_equipment_ids: comboEquipIds,
        worker_allocations: buildWorkerSlots(),
        status: existingAlloc.status || defaultStatus
      });
      setEditingAllocationId(existingAlloc.id);
    } else {
      // New allocation
      const comboEquipIds = isCombo ? comboComponents.map(comp => ({
        component_type_id: comp.id,
        component_type_name: comp.name,
        equipment_id: ''
      })) : [];

      // Find next unallocated date
      let nextDate = job.start_date;
      if (coverageStats && coverageStats.covered < coverageStats.total) {
        const excluded = new Set(job.excluded_dates || []);
        try {
          const allDates = eachDayOfInterval({ start: parseISO(job.start_date), end: parseISO(job.end_date || job.start_date) });
          const coveredDates = new Set();
          jobAllocations.forEach(a => {
            if (!a.start_date) return;
            try {
              eachDayOfInterval({ start: parseISO(a.start_date), end: parseISO(a.end_date || a.start_date) })
                .forEach(d => coveredDates.add(format(d, 'yyyy-MM-dd')));
            } catch {}
          });
          const firstUncovered = allDates.find(d => {
            const ds = format(d, 'yyyy-MM-dd');
            return !excluded.has(ds) && !coveredDates.has(ds);
          });
          if (firstUncovered) nextDate = format(firstUncovered, 'yyyy-MM-dd');
        } catch {}
      }

      setAllocationForm({
        equipment_id: '',
        operator_id: '',
        start_date: nextDate,
        end_date: job.end_date || '',
        is_combo_allocation: isCombo,
        combo_equipment_ids: comboEquipIds,
        worker_allocations: buildWorkerSlots(),
        status: defaultStatus
      });
      setEditingAllocationId(null);
    }
    setShowAllocationSheet(true);
  };

  const handleSaveAllocation = async () => {
    setSaving(true);
    try {
      const isCombo = allocationForm.is_combo_allocation;
      const comboEquipStrings = isCombo
        ? (allocationForm.combo_equipment_ids || []).map(c => c?.equipment_id).filter(Boolean)
        : [];

      if (isCombo) {
        const missing = (allocationForm.combo_equipment_ids || []).filter(c => !c?.equipment_id);
        if (missing.length > 0) {
          toast.error('Please select equipment for all combo components');
          setSaving(false);
          return;
        }
      }

      const data = {
        job_id: jobId,
        operator_id: allocationForm.operator_id,
        start_date: allocationForm.start_date,
        end_date: allocationForm.end_date || allocationForm.start_date,
        is_combo_allocation: isCombo,
        equipment_id: isCombo ? (comboEquipStrings[0] || '') : allocationForm.equipment_id,
        combo_equipment_ids: comboEquipStrings,
        status: allocationForm.status
      };

      // Worker allocations to create/update
      const waSlots = allocationForm.worker_allocations || [];
      const toCreate = waSlots.filter(wa => wa.operator_id && !wa.id);
      const toUpdate = waSlots.filter(wa => wa.operator_id && wa.id);

      // Leave preflight — only check workers who gain new days (matches backend _leave_check_needed).
      // No override on mobile: if anyone is on leave, block the save.
      const preflightItems = [];
      if (editingAllocationId) {
        // Editing: only check operator if changed or date range expanded
        const orig = allocations.find(a => a.id === editingAllocationId);
        if (data.operator_id && orig) {
          const origEnd = orig.end_date || orig.start_date;
          const operatorChanged = data.operator_id !== orig.operator_id;
          const rangeExpanded = data.start_date < orig.start_date || data.end_date > origEnd;
          if (operatorChanged || rangeExpanded) {
            preflightItems.push({ operator_id: data.operator_id, start_date: data.start_date, end_date: data.end_date });
          }
        }
      } else {
        // New allocation: always check the operator
        if (data.operator_id) {
          preflightItems.push({ operator_id: data.operator_id, start_date: data.start_date, end_date: data.end_date });
        }
      }
      // New crew: always check
      for (const wa of toCreate) {
        if (wa.operator_id) {
          preflightItems.push({ operator_id: wa.operator_id, start_date: data.start_date, end_date: data.end_date });
        }
      }
      // Updated crew: only check if operator actually changed
      for (const wa of toUpdate) {
        const origWa = workerAllocations.find(w => w.id === wa.id);
        if (wa.operator_id && wa.operator_id !== origWa?.operator_id) {
          preflightItems.push({ operator_id: wa.operator_id, start_date: data.start_date, end_date: data.end_date });
        }
      }
      if (preflightItems.length > 0) {
        try {
          const pfRes = await api.post('/api/allocations/leave-preflight', { items: preflightItems });
          const blocks = pfRes.data?.blocks || [];
          if (blocks.length > 0) {
            toast.error(blocks.map(b => b.message).join('; '));
            setSaving(false);
            return;
          }
        } catch { /* preflight failed — proceed */ }
      }

      if (editingAllocationId) {
        await api.put(`/api/allocations/${editingAllocationId}?skip_validation=true`, data);
        toast.success('Allocation updated');
      } else {
        await api.post('/api/allocations?skip_validation=true', data);
        toast.success(data.status === 'draft' ? 'Draft allocation saved' : 'Allocation confirmed');
      }

      // Save worker allocations
      if (toCreate.length > 0) {
        await api.post('/api/worker-allocations/bulk', toCreate.map(wa => ({
          job_id: jobId,
          worker_type_id: wa.worker_type_id,
          operator_id: wa.operator_id,
          start_date: allocationForm.start_date,
          end_date: allocationForm.end_date || allocationForm.start_date,
          status: allocationForm.status
        })));
      }
      for (const wa of toUpdate) {
        const origWa = workerAllocations.find(w => w.id === wa.id);
        if (wa.operator_id !== origWa?.operator_id) {
          await api.patch(`/api/worker-allocations/${wa.id}`, { operator_id: wa.operator_id });
        }
      }

      setShowAllocationSheet(false);
      fetchData();
    } catch (error) {
      // On mobile, show leave_block message as toast (no override)
      const lb = error?.response?.data?.detail;
      if (lb && typeof lb === 'object' && lb.error === 'leave_block') {
        toast.error(lb.message || 'Worker is on leave');
      } else {
        toastApiError(error, 'Failed to save allocation');
      }
    } finally { setSaving(false); }
  };

  const handleDeleteAllocation = async () => {
    if (!deletingAllocId) return;
    try {
      await api.delete(`/api/allocations/${deletingAllocId}`);
      toast.success('Allocation deleted');
      setShowDeleteAllocDialog(false);
      setDeletingAllocId(null);
      fetchData();
    } catch (error) {
      toastApiError(error, 'Failed to delete allocation');
    }
  };

  const handleConfirmJob = async () => {
    setSaving(true);
    try {
      await api.post(`/api/jobs/${jobId}/confirm`);
      toast.success('Job confirmed');
      fetchData();
    } catch (error) {
      toastApiError(error, 'Failed to confirm job');
    } finally { setSaving(false); }
  };

  const handleDuplicateJob = async () => {
    try {
      await api.post('/api/jobs', {
        customer_id: job.customer_id, site_id: job.site_id || '', equipment_type: job.equipment_type,
        start_date: job.start_date, start_time: job.start_time || null, end_date: job.end_date || null,
        description: job.description, status: 'not_allocated', notes: job.notes || '', starting_location: job.starting_location || ''
      });
      toast.success('Job duplicated');
      navigate('/mobile/schedule');
    } catch (error) { toast.error('Failed to duplicate job'); }
  };

  const handleDeleteJob = async () => {
    try {
      await api.delete(`/api/jobs/${jobId}`);
      toast.success('Job deleted');
      navigate('/mobile/schedule');
    } catch (error) { toast.error('Failed to delete job'); }
    setShowDeleteDialog(false);
  };

  // Haulage Stop Progression
  const handleStopArrive = async (stopId) => {
    try {
      const res = await api.post(`/api/jobs/${jobId}/stops/${stopId}/arrive`);
      setJob(prev => ({ ...prev, stops: res.data.stops }));
      toast.success('Marked as arrived');
    } catch (e) { toastApiError(e, 'Failed to update stop'); }
  };

  const handleStopComplete = (stopId, stopLabel) => {
    setCompletingStop({ id: stopId, label: stopLabel });
  };

  const handleStopCompleted = (stops) => {
    setJob(prev => ({ ...prev, stops }));
    setCompletingStop(null);
  };

  const handleStopDelete = (stopId, stopLabel) => {
    setDeletingStop({ id: stopId, label: stopLabel });
  };

  const handleStopDeleted = (stops, routeSummary) => {
    setJob(prev => ({ ...prev, stops, route_summary: routeSummary ?? prev.route_summary }));
    setDeletingStop(null);
  };

  const handleStopRestore = async (stopId) => {
    try {
      const res = await api.post(`/api/jobs/${jobId}/stops/${stopId}/restore`);
      setJob(prev => ({ ...prev, stops: res.data.stops, route_summary: res.data.route_summary }));
      toast.success('Stop restored');
    } catch (e) { toastApiError(e, 'Failed to restore stop'); }
  };

  const handleStopsUpdated = (stops, routeSummary) => {
    setJob(prev => ({ ...prev, stops, route_summary: routeSummary }));
  };

  const fmtTime = (t) => t ? formatTimeUtil(t, timeFormat) : 'TBD';
  const fmtDate = (d) => { try { return format(new Date(d + 'T00:00:00'), 'EEE, d MMM yyyy'); } catch { return d || ''; } };
  const fmtDateShort = (d) => { try { return format(new Date(d + 'T00:00:00'), 'd MMM'); } catch { return d || ''; } };

  const getAvailableEquipment = () => {
    if (!job?.equipment_type) return equipment;
    const matching = equipment.filter(e => e.equipment_type === job.equipment_type);
    const others = equipment.filter(e => e.equipment_type !== job.equipment_type);
    return [...matching, ...others];
  };

  const isMultiDay = job?.end_date && job?.end_date !== job?.start_date;

  // Compute effective status from actual allocation data (mirrors desktop getEffectiveStatus)
  // This overrides stale job.status values that weren't recalculated after backend fixes
  const effectiveStatus = useMemo(() => {
    if (!job) return 'not_allocated';
    if (job.is_tbc || job.status === 'tbc') return 'tbc';
    if (job.status === 'docketed' || job.status === 'invoiced') return job.status;

    if (jobAllocations.length === 0) return job.status;

    const isLabour = job.hire_type === 'labour' || job.equipment_type === 'No Asset';
    if (isLabour) return job.status;

    const isDryHire = job.hire_type === 'dry';
    const multiDay = job.end_date && job.end_date !== job.start_date;

    if (!multiDay) {
      const alloc = jobAllocations[0];
      const hasEquip = !!(alloc.equipment_id || (alloc.is_combo_allocation && alloc.combo_equipment_ids?.length));
      const hasOp = !!alloc.operator_id;
      if (!hasEquip && !hasOp) return 'not_allocated';
      if (!hasEquip || (!isDryHire && !hasOp)) return 'partially_allocated';
      const ds = (alloc.day_statuses || {})[job.start_date] || alloc.status || 'draft';
      if (ds === 'confirmed') return 'confirmed';
      if (ds === 'sent') return 'sent';
      return 'allocated';
    }

    try {
      const allDates = eachDayOfInterval({ start: parseISO(job.start_date), end: parseISO(job.end_date) });
      const excluded = new Set(job.excluded_dates || []);
      const dayStatuses = [];

      for (const day of allDates) {
        const ds = format(day, 'yyyy-MM-dd');
        if (excluded.has(ds)) continue;
        const covering = jobAllocations.find(a => a.start_date <= ds && (a.end_date || a.start_date) >= ds);
        if (!covering) { dayStatuses.push('not_allocated'); continue; }
        const hasEquip = !!(covering.equipment_id || (covering.is_combo_allocation && covering.combo_equipment_ids?.length));
        const hasOp = !!covering.operator_id;
        if (!hasEquip || (!isDryHire && !hasOp)) { dayStatuses.push('partially_allocated'); continue; }
        const status = (covering.day_statuses || {})[ds] || covering.status || 'draft';
        if (status === 'confirmed') dayStatuses.push('confirmed');
        else if (status === 'sent') dayStatuses.push('sent');
        else dayStatuses.push('allocated');
      }

      if (dayStatuses.length === 0) return 'not_allocated';
      const unique = [...new Set(dayStatuses)];
      if (unique.length === 1) return unique[0];
      if (unique.includes('confirmed')) return 'mixed';
      if (unique.every(s => s === 'allocated' || s === 'sent')) return unique.includes('sent') ? 'sent' : 'allocated';
      if (unique.includes('not_allocated')) return 'partially_allocated';
      return 'partially_allocated';
    } catch {
      return job.status;
    }
  }, [job, jobAllocations]);

  if (loading) {
    return <MobileLayout><div className="flex justify-center items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" /></div></MobileLayout>;
  }
  if (!job) {
    return <MobileLayout><div className="p-4 text-center"><p className="text-[var(--m-text-secondary)]">Job not found</p><Button onClick={() => navigate('/mobile/schedule')} className="mt-4 btn-volt">Back to Schedule</Button></div></MobileLayout>;
  }

  // Render combo equipment names for an allocation
  const renderAllocEquipment = (alloc) => {
    if (alloc.is_combo_allocation && alloc.combo_equipment_ids?.length) {
      const names = alloc.combo_equipment_ids.map(eid => {
        const id = typeof eid === 'string' ? eid : eid?.equipment_id;
        const eq = equipment.find(e => e.id === id);
        return eq ? `${eq.asset_id} (${eq.equipment_type})` : '';
      }).filter(Boolean);
      if (names.length > 0) return names;
    }
    if (alloc.equipment_id) {
      return [getEquipmentName(alloc.equipment_id)];
    }
    return [];
  };

  return (
    <MobileLayout>
      {/* Header */}
      <div className="bg-[var(--m-bg-card)] px-4 py-4 sticky top-0 z-10 safe-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/mobile/schedule')} className="text-[var(--m-text-secondary)] p-2" data-testid="schedule-detail-back-btn">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-[var(--m-text)] font-bold text-lg">Job Details</h1>
          </div>
          {!isEditing && (
            <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)} className="text-[#ffd108] p-2" data-testid="schedule-detail-edit-btn">
              <Pencil className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Status & Time */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-[#ffd108]" />
                <span className="text-2xl font-bold text-[#ffd108]">{fmtTime(job.start_time)}</span>
              </div>
              <ScheduleStatusBadge status={effectiveStatus} />
            </div>
            <div className="flex items-center gap-2 text-[var(--m-text-secondary)]">
              <Calendar className="h-4 w-4" />
              <span>{fmtDate(job.start_date)}</span>
              {isMultiDay && <><span>-</span><span>{fmtDate(job.end_date)}</span></>}
            </div>
            {job.job_number && <p className="text-[var(--m-text-muted)] text-xs mt-1">Job #{job.job_number}</p>}
            {isMultiDay && coverageStats && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-[var(--m-text-secondary)]">Coverage</span>
                  <span className={coverageStats.covered === coverageStats.total ? 'text-green-400' : 'text-amber-400'}>
                    {coverageStats.covered} / {coverageStats.total} days
                  </span>
                </div>
                <div className="h-1.5 bg-[var(--m-bg-elevated)] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${coverageStats.covered === coverageStats.total ? 'bg-green-500' : 'bg-amber-500'}`}
                    style={{ width: `${coverageStats.total > 0 ? (coverageStats.covered / coverageStats.total) * 100 : 0}%` }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {isEditing ? (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
            <CardHeader className="pb-2"><CardTitle className="text-[var(--m-text)] text-lg flex items-center gap-2"><Pencil className="h-5 w-5 text-[#ffd108]" />Edit Job</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[var(--m-text-secondary)] text-xs uppercase">Customer</Label>
                <Select value={editForm.customer_id} onValueChange={(v) => setEditForm({ ...editForm, customer_id: v, site_id: '' })}>
                  <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border)]">{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[var(--m-text-secondary)] text-xs uppercase">Site</Label>
                <Select value={editForm.site_id} onValueChange={(v) => setEditForm({ ...editForm, site_id: v })} disabled={!editForm.customer_id}>
                  <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]"><SelectValue placeholder="Select site" /></SelectTrigger>
                  <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border)]">{getCustomerSites(editForm.customer_id).map(s => <SelectItem key={s.id} value={s.id}>{s.name || s.street_address}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[var(--m-text-secondary)] text-xs uppercase">Asset Type</Label>
                <Select value={editForm.equipment_type} onValueChange={(v) => setEditForm({ ...editForm, equipment_type: v })}>
                  <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border)]">{equipmentTypes.map(t => {
                    const name = typeof t === 'string' ? t : t?.name;
                    return name ? <SelectItem key={name} value={name}>{name}</SelectItem> : null;
                  })}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label className="text-[var(--m-text-secondary)] text-xs uppercase">Start Date</Label><DatePicker value={editForm.start_date} onChange={(d) => setEditForm({ ...editForm, start_date: d })} className="w-full bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" /></div>
                <div className="space-y-2"><Label className="text-[var(--m-text-secondary)] text-xs uppercase">End Date</Label><DatePicker value={editForm.end_date || ''} onChange={(d) => setEditForm({ ...editForm, end_date: d })} className="w-full bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" minDate={editForm.start_date} /></div>
              </div>
              <div className="space-y-2"><Label className="text-[var(--m-text-secondary)] text-xs uppercase">Start Time</Label><WheelTimePicker value={editForm.start_time || ''} onChange={(v) => setEditForm({ ...editForm, start_time: v })} minuteStep={5} className="w-full bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" /></div>
              <div className="space-y-2"><Label className="text-[var(--m-text-secondary)] text-xs uppercase">Description</Label><Textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" rows={3} /></div>
              <div className="space-y-2"><Label className="text-[var(--m-text-secondary)] text-xs uppercase">Notes</Label><Textarea value={editForm.notes || ''} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" rows={2} /></div>
              {tenantSettings?.field_rules?.starting_location?.enabled && (
                <div className="space-y-2">
                  <Label className="text-[var(--m-text-secondary)] text-xs uppercase">Starting Location {tenantSettings?.field_rules?.starting_location?.required ? <span className="text-red-500">*</span> : ''}</Label>
                  <GooglePlacesAutocomplete
                    placeholder="Where will workers start from?"
                    country="AU"
                    value={editForm.starting_location || ''}
                    onChange={(e) => setEditForm({ ...editForm, starting_location: e.target.value })}
                    onPlaceSelect={(place) => setEditForm({ ...editForm, starting_location: place.formatted_address || place.street_address || '' })}
                    className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]"
                    data-testid="schedule-edit-starting-location"
                  />
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1 border-[var(--m-border)] text-[var(--m-text)]" onClick={() => setIsEditing(false)} disabled={saving}>Cancel</Button>
                <Button className="flex-1 btn-volt" onClick={handleSaveEdit} disabled={saving} data-testid="schedule-save-job-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save</Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Daily Schedule — per-day custom start/end times for multi-day jobs */}
            {isMultiDay && activeDays.length > 0 && (
              <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]" data-testid="daily-schedule-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-[var(--m-text)] text-base flex items-center gap-2">
                    <Clock className="h-5 w-5 text-[#ffd108]" />
                    Daily Schedule
                  </CardTitle>
                  <p className="text-[var(--m-text-muted)] text-xs">Tap a day to set a custom start/end time. Days without a custom time use the job default ({fmtTime(job.start_time)}{job.end_time ? ` – ${fmtTime(job.end_time)}` : ''}).</p>
                </CardHeader>
                <CardContent className="space-y-2">
                  {activeDays.map((ds, idx) => {
                    const dt = resolveDayTimes(ds);
                    return (
                      <button
                        key={ds}
                        onClick={() => openDayEdit(ds)}
                        className="w-full flex items-center justify-between p-3 rounded-lg border border-[var(--m-border)] bg-[var(--m-bg)] hover:border-[#ffd108]/50 transition-colors text-left"
                        data-testid={`day-schedule-row-${ds}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Calendar className="h-4 w-4 text-[var(--m-text-muted)] flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-[var(--m-text)] text-sm font-medium">Day {idx + 1} <span className="text-[var(--m-text-secondary)] font-normal">· {fmtDateShort(ds)}</span></p>
                            <p className={`text-xs font-mono ${dt.overridden ? 'text-amber-400' : 'text-[var(--m-text-secondary)]'}`}>
                              {fmtTime(dt.start_time)}{dt.end_time ? ` – ${fmtTime(dt.end_time)}` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {dt.overridden && (
                            <Badge className="bg-amber-900/50 text-amber-300 text-[10px] border-0" data-testid={`day-schedule-custom-badge-${ds}`}>Custom</Badge>
                          )}
                          <Pencil className="h-3.5 w-3.5 text-[#ffd108]" />
                        </div>
                      </button>
                    );
                  })}
                </CardContent>
              </Card>
            )}
            {/* Info Cards */}
            <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start gap-3"><Building2 className="h-5 w-5 text-[#ffd108] mt-0.5" /><div><p className="text-[var(--m-text)] font-medium">{getCustomerName(job.customer_id)}</p><p className="text-[var(--m-text-secondary)] text-sm">Customer</p></div></div>
                {(job.job_type === 'haulage' || job.requires_multi_stops) && job.route_summary ? (
                  <div className="flex items-start gap-3"><MapPin className="h-5 w-5 text-[#ffd108] mt-0.5" /><div><p className="text-[var(--m-text)] font-medium">{job.route_summary}</p><p className="text-[var(--m-text-secondary)] text-sm">Route</p></div></div>
                ) : job.site_id && (
                  <div className="flex items-start gap-3"><MapPin className="h-5 w-5 text-[#ffd108] mt-0.5" /><div><p className="text-[var(--m-text)] font-medium">{getSiteName(job.site_id)}</p><p className="text-[var(--m-text-secondary)] text-sm">{getSiteAddress(job.site_id)}</p></div></div>
                )}
                {job.equipment_type && (
                  <div className="flex items-start gap-3">
                    <Truck className="h-5 w-5 text-[#ffd108] mt-0.5" />
                    <div>
                      <p className="text-[var(--m-text)] font-medium">{job.equipment_type}</p>
                      <p className="text-[var(--m-text-secondary)] text-sm">{isComboType ? 'Combo Asset' : 'Asset Required'}</p>
                    </div>
                  </div>
                )}
                {job.starting_location && (
                  <div className="flex items-start gap-3" data-testid="schedule-detail-starting-location"><Navigation className="h-5 w-5 text-[#ffd108] mt-0.5" /><div><p className="text-[var(--m-text)] font-medium">{job.starting_location}</p><p className="text-[var(--m-text-secondary)] text-sm">Starting Location</p></div></div>
                )}
              </CardContent>
            </Card>
            {job.description && <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]"><CardContent className="p-4"><p className="text-[var(--m-text-secondary)] text-xs uppercase mb-2">Description</p><p className="text-[var(--m-text)] whitespace-pre-wrap break-words">{job.description}</p></CardContent></Card>}

            {/* Haulage / Multi-Stop Route Stops */}
            {(job.job_type === 'haulage' || job.requires_multi_stops) && job.stops && job.stops.length > 0 && (
              <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-[var(--m-text)] text-base flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-[#ffd108]" />
                    Route ({job.stops.filter(s => !s.deleted && s.status === 'completed').length}/{job.stops.filter(s => !s.deleted).length} completed)
                  </CardTitle>
                  {job.route_summary && <p className="text-[var(--m-text-secondary)] text-xs">{job.route_summary}</p>}
                </CardHeader>
                <CardContent className="space-y-2">
                  {[...job.stops].sort((a, b) => a.order - b.order).map((stop, idx) => {
                    const isPickup = stop.type === 'pickup';
                    const isDropoff = stop.type === 'dropoff';
                    const StopIcon = isPickup ? Package : isDropoff ? Flag : Navigation;
                    const iconColor = stop.status === 'completed' ? 'text-green-400' : stop.status === 'arrived' ? 'text-blue-400' : 'text-[var(--m-text-muted)]';
                    if (stop.deleted) {
                      return (
                        <div key={stop.id || idx} className="p-3 rounded-lg border border-red-800/50 bg-red-950/20" data-testid={`route-stop-${idx}`}>
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-red-900/40">
                              <StopIcon className="h-4 w-4 text-red-400/70" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-[10px] border-red-600 text-red-400">{stop.type}</Badge>
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-red-400" data-testid={`stop-${idx}-deleted-badge`}>Deleted</span>
                              </div>
                              <p className="text-[var(--m-text-secondary)] text-sm font-medium truncate mt-0.5 line-through">{stop.label || stop.suburb || stop.address || `Stop ${idx + 1}`}</p>
                              <p className="text-red-300/80 text-xs mt-1" data-testid={`stop-${idx}-deleted-reason`}>Deleted by {stop.deleted_by_name || 'Driver'}{stop.deleted_reason ? ` — ${stop.deleted_reason}` : ''}</p>
                            </div>
                            <Button size="sm" variant="outline" className="h-8 px-3 border-[var(--m-border-light)] text-[var(--m-text-body)] hover:bg-[var(--m-bg-elevated)]/30 flex-shrink-0" onClick={() => handleStopRestore(stop.id)} data-testid={`stop-${idx}-restore-btn`}>
                              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Undo
                            </Button>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={stop.id || idx} className={`p-3 rounded-lg border ${stop.status === 'completed' ? 'border-green-700/50 bg-green-900/10' : stop.status === 'arrived' ? 'border-blue-700/50 bg-blue-900/10' : 'border-[var(--m-border)] bg-[var(--m-bg)]'}`} data-testid={`route-stop-${idx}`}>
                        <div className="flex items-center gap-3">
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${stop.status === 'completed' ? 'bg-green-900/50' : stop.status === 'arrived' ? 'bg-blue-900/50' : 'bg-[var(--m-bg-card)]'}`}>
                            <StopIcon className={`h-4 w-4 ${iconColor}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={`text-[10px] ${stop.status === 'completed' ? 'border-green-600 text-green-400' : stop.status === 'arrived' ? 'border-blue-600 text-blue-400' : 'border-[var(--m-border-light)] text-[var(--m-text-secondary)]'}`}>
                                {stop.type}
                              </Badge>
                              {stop.est_arrival && <span className="text-[var(--m-text-muted)] text-xs">ETA {stop.est_arrival}</span>}
                              <StopArrivalBadge stop={stop} dark />
                            </div>
                            <p className="text-[var(--m-text)] text-sm font-medium truncate mt-0.5">{stop.label || stop.suburb || stop.address || `Stop ${idx + 1}`}</p>
                            {stop.notes && <p className="text-[var(--m-text-secondary)] text-xs truncate">{stop.notes}</p>}
                            {(stop.site_contact?.name || stop.site_contact_name) && (
                              <div className="flex items-center gap-1.5 mt-1 text-xs" data-testid={`stop-${idx}-site-contact`}>
                                <User className="h-3 w-3 text-[#ffd108] flex-shrink-0" />
                                {stop.site_contact?.phone ? (
                                  <a href={`tel:${stop.site_contact.phone}`} className="text-[#ffd108] truncate" onClick={(e) => e.stopPropagation()}>
                                    {stop.site_contact.name}<span className="text-[var(--m-text-secondary)]"> · {stop.site_contact.phone}</span>
                                  </a>
                                ) : (
                                  <span className="text-[var(--m-text-body)] truncate">{stop.site_contact?.name || stop.site_contact_name}</span>
                                )}
                              </div>
                            )}
                          </div>
                          {stop.status === 'completed' && <CheckCircle2 className="h-5 w-5 text-green-400 flex-shrink-0" />}
                        </div>
                        {stop.status === 'pending' && (
                          <Button size="sm" className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-[var(--m-text)]" onClick={() => handleStopArrive(stop.id)} data-testid={`stop-${idx}-arrive-btn`}>
                            <MapPin className="h-3.5 w-3.5 mr-1" /> Mark Arrived
                          </Button>
                        )}
                        {stop.status === 'arrived' && (
                          <Button size="sm" className="w-full mt-2 bg-green-600 hover:bg-green-700 text-[var(--m-text)]" onClick={() => handleStopComplete(stop.id, stop.suburb || stop.address || stop.label || `Stop ${idx + 1}`)} data-testid={`stop-${idx}-complete-btn`}>
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Complete Stop
                          </Button>
                        )}
                        <EditRouteStopDialog jobId={jobId} stop={stop} api={api} onUpdated={handleStopsUpdated} country={job.country || 'AU'} />
                        {stop.status !== 'completed' && (
                          <Button size="sm" variant="outline" className="w-full mt-2 border-red-700 text-red-400 hover:bg-red-900/20" onClick={() => handleStopDelete(stop.id, stop.label || stop.suburb || stop.address || `Stop ${idx + 1}`)} data-testid={`stop-${idx}-delete-btn`}>
                            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete Stop
                          </Button>
                        )}
                      </div>
                    );
                  })}
                  <AddRouteStopDialog
                    jobId={jobId}
                    stops={job.stops}
                    api={api}
                    onUpdated={handleStopsUpdated}
                    country={job.country || 'AU'}
                  />
                  <CompleteStopDialog
                    open={!!completingStop}
                    onOpenChange={(v) => { if (!v) setCompletingStop(null); }}
                    jobId={jobId}
                    stopId={completingStop?.id}
                    stopLabel={completingStop?.label}
                    api={api}
                    onCompleted={handleStopCompleted}
                  />
                  <DeleteStopDialog
                    open={!!deletingStop}
                    onOpenChange={(v) => { if (!v) setDeletingStop(null); }}
                    jobId={jobId}
                    stopId={deletingStop?.id}
                    stopLabel={deletingStop?.label}
                    api={api}
                    onDeleted={handleStopDeleted}
                  />
                </CardContent>
              </Card>
            )}

            {/* Allocations */}
            <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-[var(--m-text)] text-lg flex items-center gap-2">
                    <Users className="h-5 w-5 text-[#ffd108]" />
                    Allocations
                  </CardTitle>
                  <div className="flex items-center gap-1">
                    {(tenantSettings?.worker_job_email_enabled || workerAckEnabled || notifyOperatorEnabled) && jobAllocations.length > 0 && (
                      <Button size="sm" variant="ghost" className="text-[#ffd108] h-8 px-2" onClick={openEmailSheet} data-testid="schedule-email-workers-btn">
                        <Bell className="h-4 w-4 mr-1" />Notify
                      </Button>
                    )}
                    {jobAllocations.length > 0 && (
                      <Button size="sm" variant="ghost" className="text-[#ffd108] h-8 px-2" onClick={() => handleOpenAllocation(null)} data-testid="schedule-add-allocation-btn">
                        <Plus className="h-4 w-4 mr-1" />Add
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {jobAllocations.length > 0 ? (
                  <>
                    {jobAllocations.map((alloc, idx) => {
                      const equipNames = renderAllocEquipment(alloc);
                      const allocStatus = alloc.status || 'draft';
                      return (
                        <div
                          key={alloc.id}
                          className="p-3 bg-[var(--m-bg)] rounded-lg border border-[var(--m-border)] cursor-pointer hover:border-[#ffd108]/50 transition-colors"
                          onClick={() => handleOpenAllocation(alloc)}
                          data-testid={`allocation-block-${idx}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-3.5 w-3.5 text-[var(--m-text-muted)]" />
                              <span className="text-[var(--m-text)] text-sm font-medium">
                                {fmtDateShort(alloc.start_date)}
                                {alloc.end_date && alloc.end_date !== alloc.start_date && ` - ${fmtDateShort(alloc.end_date)}`}
                              </span>
                            </div>
                            <Badge className={`text-[10px] border-0 ${
                              allocStatus === 'confirmed' ? 'bg-green-900/50 text-green-300' :
                              allocStatus === 'sent' ? 'bg-amber-900/50 text-amber-300' :
                              'bg-[var(--m-bg-elevated)]/50 text-[var(--m-text-body)]'
                            }`}>
                              {allocStatus === 'confirmed' ? 'Confirmed' : allocStatus === 'sent' ? 'Sent' : 'Draft'}
                            </Badge>
                          </div>
                          {equipNames.length > 0 && (
                            <div className="space-y-1 mb-1.5">
                              {equipNames.map((name, i) => (
                                <div key={i} className="flex items-center gap-2">
                                  <Truck className="h-3.5 w-3.5 text-green-400 flex-shrink-0" />
                                  <span className="text-[var(--m-text-body)] text-xs truncate">{name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {alloc.operator_id && (
                            <div className="flex items-center gap-2">
                              <User className="h-3.5 w-3.5 text-blue-400 flex-shrink-0" />
                              <span className="text-[var(--m-text-body)] text-xs">{getOperatorName(alloc.operator_id)}</span>
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-[var(--m-border)]/50">
                            <button
                              className="text-[#ffd108] text-xs flex items-center gap-1"
                              onClick={(e) => { e.stopPropagation(); handleOpenAllocation(alloc); }}
                            >
                              <Pencil className="h-3 w-3" /> Edit
                            </button>
                            <button
                              className="text-red-400 text-xs flex items-center gap-1"
                              onClick={(e) => { e.stopPropagation(); setDeletingAllocId(alloc.id); setShowDeleteAllocDialog(true); }}
                            >
                              <Trash2 className="h-3 w-3" /> Remove
                            </button>
                          </div>
                          {workerAckEnabled && alloc.operator_id && (allocStatus === 'sent' || alloc.requires_re_ack) && (
                            <button
                              className="w-full mt-2 pt-2 border-t border-[var(--m-border)]/50 text-green-400 text-xs flex items-center justify-center gap-1 disabled:opacity-50"
                              disabled={ackBusyId === alloc.id}
                              onClick={(e) => { e.stopPropagation(); adminConfirmAlloc(alloc); }}
                              data-testid={`mobile-admin-confirm-${idx}`}
                            >
                              {ackBusyId === alloc.id
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <CheckCircle2 className="h-3.5 w-3.5" />}
                              {alloc.requires_re_ack ? 'Re-ack required — Mark confirmed' : 'Mark worker confirmed'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div className="text-center py-4">
                    <AlertTriangle className="h-10 w-10 mx-auto text-amber-400 mb-2" />
                    <p className="text-[var(--m-text-secondary)] text-sm mb-3">No resources allocated</p>
                    <Button className="btn-volt" onClick={() => handleOpenAllocation(null)} data-testid="schedule-allocate-btn">
                      <Users className="h-4 w-4 mr-2" />Allocate Resources
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="space-y-3">
              {(effectiveStatus === 'allocated' || effectiveStatus === 'sent') && (
                <Button className="w-full bg-green-600 hover:bg-green-700 text-[var(--m-text)]" onClick={handleConfirmJob} disabled={saving} data-testid="schedule-confirm-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}Confirm Job
                </Button>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" className="border-[var(--m-border)] text-[var(--m-text)]" onClick={handleDuplicateJob} data-testid="schedule-duplicate-btn"><Copy className="h-4 w-4 mr-2" />Duplicate</Button>
                <Button variant="outline" className="border-red-800 text-red-400 hover:bg-red-900/20" onClick={() => setShowDeleteDialog(true)} data-testid="schedule-delete-btn"><Trash2 className="h-4 w-4 mr-2" />Delete</Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Allocation Sheet */}
      <Sheet open={showAllocationSheet} onOpenChange={setShowAllocationSheet}>
        <SheetContent side="bottom" className="bg-[var(--m-bg-card)] border-[var(--m-border)] h-[85vh]">
          <SheetHeader>
            <SheetTitle className="text-[var(--m-text)] flex items-center gap-2">
              <Users className="h-5 w-5 text-[#ffd108]" />
              {editingAllocationId ? 'Edit Allocation' : 'New Allocation'}
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-4 py-4 overflow-y-auto max-h-[calc(85vh-140px)]">
            {/* Date range */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-[var(--m-text-secondary)] text-xs uppercase">Start Date <span className="text-red-500">*</span></Label>
                <DatePicker value={allocationForm.start_date} onChange={(d) => setAllocationForm({ ...allocationForm, start_date: d })} className="w-full bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" minDate={job.start_date} maxDate={job.end_date || undefined} />
              </div>
              <div className="space-y-2">
                <Label className="text-[var(--m-text-secondary)] text-xs uppercase">End Date</Label>
                <DatePicker value={allocationForm.end_date || ''} onChange={(d) => setAllocationForm({ ...allocationForm, end_date: d })} className="w-full bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" minDate={allocationForm.start_date} maxDate={job.end_date || undefined} />
                <p className="text-[var(--m-text-muted)] text-[10px]">Leave blank for single day</p>
              </div>
            </div>

            {/* Combo Asset Selection */}
            {allocationForm.is_combo_allocation && comboComponents.length > 0 ? (
              <div className="space-y-3">
                <Label className="text-[var(--m-text-secondary)] text-xs uppercase">Combo Assets</Label>
                {allocationForm.combo_equipment_ids.map((comp, idx) => (
                  <div key={idx} className="space-y-1">
                    <Label className="text-[var(--m-text-muted)] text-xs">{comp.component_type_name}</Label>
                    <Select
                      value={comp.equipment_id}
                      onValueChange={(v) => {
                        const updated = [...allocationForm.combo_equipment_ids];
                        updated[idx] = { ...updated[idx], equipment_id: v };
                        setAllocationForm({ ...allocationForm, combo_equipment_ids: updated });
                      }}
                    >
                      <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]">
                        <SelectValue placeholder={`Select ${comp.component_type_name}`} />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
                        {(() => {
                          const matching = equipment.filter(e => e.equipment_type === comp.component_type_name);
                          const others = equipment.filter(e => e.equipment_type !== comp.component_type_name);
                          return <>
                            {matching.map(eq => (
                              <SelectItem key={eq.id} value={eq.id} className="text-[#ffd108]">
                                {eq.asset_id} ({eq.equipment_type})
                              </SelectItem>
                            ))}
                            {matching.length > 0 && others.length > 0 && (
                              <div className="px-2 py-1 text-[10px] text-[var(--m-text-muted)] border-t border-[var(--m-border)] mt-1 pt-1">Other assets</div>
                            )}
                            {others.map(eq => (
                              <SelectItem key={eq.id} value={eq.id}>
                                {eq.asset_id} ({eq.equipment_type})
                              </SelectItem>
                            ))}
                          </>;
                        })()}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-[var(--m-text-secondary)] text-xs uppercase">Asset</Label>
                <Select value={allocationForm.equipment_id} onValueChange={(v) => setAllocationForm({ ...allocationForm, equipment_id: v })}>
                  <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]"><SelectValue placeholder="Select equipment" /></SelectTrigger>
                  <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
                    {(() => {
                      const matching = equipment.filter(e => e.equipment_type === job?.equipment_type);
                      const others = equipment.filter(e => e.equipment_type !== job?.equipment_type);
                      return <>
                        {matching.map(eq => (
                          <SelectItem key={eq.id} value={eq.id} className="text-[#ffd108]">
                            {eq.asset_id} ({eq.equipment_type})
                          </SelectItem>
                        ))}
                        {matching.length > 0 && others.length > 0 && (
                          <div className="px-2 py-1 text-[10px] text-[var(--m-text-muted)] border-t border-[var(--m-border)] mt-1 pt-1">Other assets</div>
                        )}
                        {others.map(eq => (
                          <SelectItem key={eq.id} value={eq.id}>
                            {eq.asset_id} ({eq.equipment_type})
                          </SelectItem>
                        ))}
                      </>;
                    })()}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Worker */}
            <div className="space-y-2">
              <Label className="text-[var(--m-text-secondary)] text-xs uppercase">Worker <span className="text-red-500">*</span></Label>
              <Select value={allocationForm.operator_id} onValueChange={(v) => setAllocationForm({ ...allocationForm, operator_id: v })}>
                <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]"><SelectValue placeholder="Select worker" /></SelectTrigger>
                <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border)]">{operators.map(op => <SelectItem key={op.id} value={op.id}>{op.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* Additional Worker Allocations (from job worker_requirements) */}
            {allocationForm.worker_allocations?.length > 0 && (
              <div className="space-y-3">
                <Label className="text-[var(--m-text-secondary)] text-xs uppercase">Additional Workers</Label>
                {allocationForm.worker_allocations.map((wa, idx) => (
                  <div key={idx} className="space-y-1">
                    <Label className="text-[var(--m-text-muted)] text-xs">{wa.worker_type_name}</Label>
                    <Select
                      value={wa.operator_id || '__none__'}
                      onValueChange={(v) => {
                        const updated = [...allocationForm.worker_allocations];
                        updated[idx] = { ...updated[idx], operator_id: v === '__none__' ? '' : v };
                        setAllocationForm({ ...allocationForm, worker_allocations: updated });
                      }}
                    >
                      <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]">
                        <SelectValue placeholder={`Select ${wa.worker_type_name}`} />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
                        <SelectItem value="__none__">Not assigned</SelectItem>
                        {operators.map(op => (
                          <SelectItem key={op.id} value={op.id}>{op.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}

            {/* Save as */}
            <div className="p-3 bg-[var(--m-bg)] rounded-lg border border-[var(--m-border)]">
              <Label className="text-[var(--m-text-secondary)] text-xs uppercase mb-2 block">Save as</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="allocStatus"
                    checked={allocationForm.status === 'draft'}
                    onChange={() => setAllocationForm({ ...allocationForm, status: 'draft' })}
                    className="accent-[#ffd108]"
                  />
                  <span className="text-[var(--m-text-body)] text-sm">Draft</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="allocStatus"
                    checked={allocationForm.status === 'confirmed'}
                    onChange={() => setAllocationForm({ ...allocationForm, status: 'confirmed' })}
                    className="accent-[#ffd108]"
                  />
                  <span className="text-green-400 text-sm">Confirmed</span>
                </label>
              </div>
            </div>
          </div>
          <SheetFooter className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1 border-[var(--m-border)] text-[var(--m-text)]" onClick={() => setShowAllocationSheet(false)} disabled={saving}>Cancel</Button>
            <Button
              className="flex-1 btn-volt"
              onClick={handleSaveAllocation}
              disabled={saving || (
                !allocationForm.is_combo_allocation
                  ? (!allocationForm.equipment_id && !allocationForm.operator_id)
                  : (allocationForm.combo_equipment_ids || []).some(c => !c?.equipment_id) && !allocationForm.operator_id
              )}
              data-testid="schedule-save-allocation-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              {editingAllocationId ? 'Update' : 'Save'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Job Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <AlertDialogHeader><AlertDialogTitle className="text-[var(--m-text)]">Delete Job?</AlertDialogTitle><AlertDialogDescription className="text-[var(--m-text-secondary)]">This will permanently delete this job. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="border-[var(--m-border)] text-[var(--m-text)] bg-[var(--m-bg-card)]">Cancel</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDeleteJob}>Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Allocation Dialog */}
      <AlertDialog open={showDeleteAllocDialog} onOpenChange={setShowDeleteAllocDialog}>
        <AlertDialogContent className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <AlertDialogHeader><AlertDialogTitle className="text-[var(--m-text)]">Remove Allocation?</AlertDialogTitle><AlertDialogDescription className="text-[var(--m-text-secondary)]">This will remove this allocation block from the job.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="border-[var(--m-border)] text-[var(--m-text)] bg-[var(--m-bg-card)]" onClick={() => setDeletingAllocId(null)}>Cancel</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDeleteAllocation}>Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Notify workers of job details */}
      <Sheet open={showEmailSheet} onOpenChange={setShowEmailSheet}>
        <SheetContent side="bottom" className="bg-[var(--m-bg)] border-[var(--m-border)] rounded-t-2xl max-h-[80vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-[var(--m-text)] flex items-center gap-2">
              <Bell className="h-5 w-5 text-[#ffd108]" /> Notify workers
            </SheetTitle>
          </SheetHeader>
          <div className="py-4 space-y-2">
            {emailLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[var(--m-text-secondary)]" /></div>
            ) : emailRecipients.length === 0 ? (
              <p className="text-[var(--m-text-secondary)] text-sm text-center py-6">No workers allocated to this job yet.</p>
            ) : emailRecipients.map(r => (
              <button
                key={r.operator_id}
                onClick={() => setEmailSelected(prev => ({ ...prev, [r.operator_id]: !prev[r.operator_id] }))}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left ${emailSelected[r.operator_id] ? 'border-[#ffd108]/60 bg-[#ffd108]/10' : 'border-[var(--m-border)] bg-[var(--m-bg-card)]'}`}
                data-testid={`mobile-email-recipient-${r.operator_id}`}
              >
                <div className={`h-5 w-5 rounded border flex items-center justify-center ${emailSelected[r.operator_id] ? 'bg-[#ffd108] border-[#ffd108]' : 'border-[var(--m-border-light)]'}`}>
                  {emailSelected[r.operator_id] && <Check className="h-3.5 w-3.5 text-black" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[var(--m-text)] text-sm font-medium truncate">{r.name}</p>
                  <p className="text-[var(--m-text-secondary)] text-xs truncate">{r.email || 'No email address'}</p>
                </div>
                <span className="text-[var(--m-text-secondary)] text-xs whitespace-nowrap capitalize">
                  {r.notification_preference === 'both' ? 'Email + Push' : (r.notification_preference || 'email')}
                </span>
              </button>
            ))}
          </div>
          {emailRecipients.some(r => emailSelected[r.operator_id] && (r.notification_preference === 'push' || r.notification_preference === 'both')) && (
            <div className="space-y-2 border-t border-[var(--m-border)] pt-3 pb-2">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-[var(--m-text-body)] text-xs flex items-center gap-1.5">
                  <Bell className="h-3.5 w-3.5" /> Push message
                </Label>
                {emailTemplates.length > 0 && (
                  <Select value={emailTemplateId} onValueChange={(id) => { const t = emailTemplates.find(x => x.id === id); if (t) applyTemplate(t); }}>
                    <SelectTrigger className="h-8 w-40 text-xs bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]" data-testid="mobile-notify-template-select">
                      <SelectValue placeholder="Template" />
                    </SelectTrigger>
                    <SelectContent>
                      {emailTemplates.map(t => (
                        <SelectItem key={t.id} value={t.id} className="text-xs">{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <Input
                className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] text-sm"
                value={pushTitle}
                onChange={(e) => setPushTitle(e.target.value)}
                placeholder="Notification title"
                data-testid="mobile-notify-push-title"
              />
              <Textarea
                className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] text-sm"
                rows={3}
                value={pushMessage}
                onChange={(e) => setPushMessage(e.target.value)}
                placeholder="Notification message"
                data-testid="mobile-notify-push-message"
              />
              <p className="text-[10px] text-[var(--m-text-muted)]">Email recipients get the standard job-details email.</p>
            </div>
          )}
          <SheetFooter>
            <Button className="btn-volt w-full" onClick={handleSendWorkerEmails} disabled={emailSending} data-testid="mobile-send-worker-email-btn">
              {emailSending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bell className="h-4 w-4 mr-2" />}
              Notify Workers
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Per-day custom time edit */}
      <Sheet open={dayEditOpen} onOpenChange={setDayEditOpen}>
        <SheetContent side="bottom" className="bg-[var(--m-bg)] border-[var(--m-border)]" data-testid="day-time-edit-sheet">
          <SheetHeader>
            <SheetTitle className="text-[var(--m-text)] flex items-center gap-2">
              <Clock className="h-5 w-5 text-[#ffd108]" />
              Custom Time — {dayEditDate ? fmtDateShort(dayEditDate) : ''}
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <p className="text-[var(--m-text-muted)] text-xs">Set a custom start/end time for this day only. Leave blank to use the job default ({fmtTime(job.start_time)}{job.end_time ? ` – ${fmtTime(job.end_time)}` : ''}).</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-[var(--m-text-secondary)] text-xs uppercase">Start Time</Label>
                <WheelTimePicker value={dayEditForm.start_time} onChange={(v) => setDayEditForm(f => ({ ...f, start_time: v }))} minuteStep={5} className="w-full bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]" testId="day-time-start-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-[var(--m-text-secondary)] text-xs uppercase">End Time</Label>
                <WheelTimePicker value={dayEditForm.end_time} onChange={(v) => setDayEditForm(f => ({ ...f, end_time: v }))} minuteStep={5} className="w-full bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]" testId="day-time-end-input" />
              </div>
            </div>
          </div>
          <SheetFooter className="flex-col gap-2 sm:flex-col">
            <Button className="btn-volt w-full" onClick={handleSaveDayTime} disabled={dayEditSaving} data-testid="day-time-save-btn">
              {dayEditSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
            {dayEditDate && jobDayDetails[dayEditDate]?.id && (
              <Button variant="outline" className="w-full border-[var(--m-border)] text-[var(--m-text-body)]" onClick={handleResetDayTime} disabled={dayEditSaving} data-testid="day-time-reset-btn">
                <X className="h-4 w-4 mr-2" />
                Reset to Job Default
              </Button>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </MobileLayout>
  );
};

const ScheduleStatusBadge = ({ status }) => {
  const styles = {
    not_allocated: { bg: 'bg-red-900/50', text: 'text-red-300', label: 'Not Allocated' },
    partially_allocated: { bg: 'bg-orange-900/50', text: 'text-orange-300', label: 'Partial' },
    allocated: { bg: 'bg-blue-900/50', text: 'text-blue-300', label: 'Allocated' },
    sent: { bg: 'bg-amber-900/50', text: 'text-amber-300', label: 'Sent' },
    confirmed: { bg: 'bg-green-900/50', text: 'text-green-300', label: 'Confirmed' },
    mixed: { bg: 'bg-orange-900/50', text: 'text-orange-300', label: 'Mixed' },
    tbc: { bg: 'bg-[var(--m-bg-elevated)]/50', text: 'text-[var(--m-text-body)]', label: 'TBC' },
    docketed: { bg: 'bg-cyan-900/50', text: 'text-cyan-300', label: 'Docketed' },
    invoiced: { bg: 'bg-[var(--m-bg-elevated)]/50', text: 'text-[var(--m-text-body)]', label: 'Invoiced' },
  };
  const s = styles[status] || { bg: 'bg-[var(--m-bg-elevated)]/50', text: 'text-[var(--m-text-body)]', label: status };
  return <Badge className={`${s.bg} ${s.text} text-xs border-0`}>{s.label}</Badge>;
};

export default MobileScheduleJobDetail;
